import { sectionRenderer } from '@theme/section-renderer';

/**
 * Renders a "you might also like" rail in the cart drawer and handles adding
 * a recommended product straight from its card, without leaving the drawer.
 *
 * The recommendation logic itself lives entirely server-side in
 * snippets/cross-sell-drawer.liquid — this component only wires up the
 * "add to cart" buttons and refreshes the drawer afterwards, the same way
 * gift-threshold-watcher.js does for the milestone gift.
 *
 * Built for VeCandles, independent of any third-party app.
 */
class CartCrossSell extends HTMLElement {
  /**
   * A single delegated listener on the component itself, instead of one per
   * button. Every successful add re-renders this rail's cards through
   * sectionRenderer.renderSection() (see #onAdd below), and the DOM morph
   * that applies the fresh HTML only reuses an existing <button> node when
   * the card list is exactly the same length, in the same order, as before.
   * As soon as the recommended collection changes (e.g. adding a wax melt
   * switches the rail from "favoritas" to "quemadores de cera") the list can
   * come back a different length, and morph.js then appends genuinely new
   * <button> nodes straight from the parsed HTML — nodes that never had a
   * per-button listener attached, so clicking them did nothing. Delegating
   * to `this` (the <cart-cross-sell> element itself, which morph always
   * reuses in place, never replaces) means every card's button works no
   * matter how many times its surrounding markup has been replaced.
   */
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-cross-sell-add]'));
    if (!button) return;
    this.#onAdd(button);
  };

  /** @param {HTMLButtonElement} button */
  #onAdd = async (button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.dataset.state === 'adding') return;

    button.dataset.state = 'adding';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed with status ${response.status}`);
      }

      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      /** @type {any} */
      const cartIcon = document.querySelector('cart-icon');
      cartIcon?.renderCartBubble?.(cart.item_count);

      button.dataset.state = 'added';

      const sectionId = this.dataset.cartDrawerSectionId || 'cart-drawer-section';
      // injectStylesheet: true keeps the section's <style> in sync with whatever
      // this fresh render actually needs — the same defensive step the theme's
      // own cart-items-component takes whenever it re-renders this section
      // outside of a plain quantity change.
      await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      console.warn('[cart-cross-sell] Failed to add product:', error);
      button.dataset.state = '';
      button.disabled = false;
    }
  };
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
